import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { LoadingScreen } from '@/components/LoadingScreen'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { GameModesPage } from '@/pages/GameModesPage'
import { useHomeBootstrap, useRecentMatches, type HomeBootstrap, type HomeRecentMatches } from '@/features/home/api'
import {
  getPendingCelebrations,
  hasPendingCelebrations,
  markCelebrationsSeen,
  type PendingCelebrationsResponse,
} from '@/features/celebrations/api'
import { showBrowserConfetti } from '@/features/celebrations/celebrationService'
import { useTranslation } from '@/i18n'

type Dashboard = NonNullable<HomeBootstrap['dashboard']>
type DashboardPoule = Dashboard['poules'][number]
type DashboardMatch = DashboardPoule['matches'][number]
type RecentPoule = HomeRecentMatches['poules'][number]
type RecentMatch = RecentPoule['matches'][number]
type SuccessMatch = DashboardMatch | RecentMatch
type PerfectCelebration = { match: SuccessMatch; pouleName: string }
type DashboardView = 'dashboard' | 'success'

const MOCK_TEAM_LOGOS = [
  'https://cdn.sportmonks.com/images/soccer/teams/19/83.png',
  'https://cdn.sportmonks.com/images/soccer/team_placeholder.png',
]

function mockTeam(id: number, name: string, logoUrl: string) {
  return { id, name, display_name: name, short_name: name.slice(0, 3).toUpperCase(), logo_url: logoUrl }
}

const MOCK_MATCHES = Array.from({ length: 27 }, (_, index) => {
  const homeName = ['Ajax', 'PSV', 'Feyenoord', 'FC Utrecht'][index % 4] ?? 'Team thuis'
  const awayName = ['AZ', 'FC Twente', 'RKC Waalwijk', 'Go Ahead Eagles'][index % 4] ?? 'Team uit'
  const pouleId = index < 14 ? 101 : 102
  const points = [10, 5, 0, 8, 0, 10, 5][index % 7]
  return {
    id: 9000 + index,
    kickoff_utc: `2026-10-15T${String(12 + (index % 10)).padStart(2, '0')}:00:00Z`,
    kickoff_nl: `2026-10-15T${String(14 + (index % 8)).padStart(2, '0')}:00:00+02:00`,
    status: 'ft',
    predict_status: 'closed',
    is_predictable: false,
    round: 12,
    poule_id: pouleId,
    poule_match_id: 7000 + index,
    score: { home: index % 4, away: (index + 1) % 3 },
    home_team: mockTeam(index * 2 + 1, homeName, MOCK_TEAM_LOGOS[index % 2] ?? MOCK_TEAM_LOGOS[0] ?? ''),
    away_team: mockTeam(index * 2 + 2, awayName, MOCK_TEAM_LOGOS[(index + 1) % 2] ?? MOCK_TEAM_LOGOS[0] ?? ''),
    user_prediction: {
      id: 8000 + index,
      home_pred: index % 3,
      away_pred: (index + 1) % 3,
      points_awarded: points,
      poule_id: pouleId,
    },
  }
}) as DashboardMatch[]

const MOCK_DASHBOARD = {
  user_id: 111,
  date: '2026-10-15',
  points_date: '2026-10-15',
  total_points: MOCK_MATCHES.reduce((sum, match) => sum + (match.user_prediction?.points_awarded ?? 0), 0),
  matches_count: 27,
  badges_earned_today_count: 2,
  prediction_counts: { perfect: 4, wrong: 2 },
  poules: [
    { id: 101, name: 'Vriendenpoule', thumbnail_url: 'https://cdn.sportmonks.com/images/soccer/teams/19/83.png', matches: MOCK_MATCHES.slice(0, 14) },
    { id: 102, name: 'KKD 2026/2027', thumbnail_url: 'https://cdn.sportmonks.com/images/soccer/team_placeholder.png', matches: MOCK_MATCHES.slice(14) },
  ],
  badges_earned_today: [
    { id: 1, name: 'Scherpschutter', icon_url: null },
    { id: 2, name: 'Op dreef', icon_url: null },
  ],
} as Dashboard

const MOCK_RECENT_MATCHES = {
  user_id: 111,
  date: '2026-10-15',
  total_points: MOCK_DASHBOARD.total_points,
  poules: MOCK_DASHBOARD.poules,
} as HomeRecentMatches

const MOCK_MANAGER_EVENTS = [
  {
    event_id: 501,
    type: 'fantasy_round_completed',
    league: { id: 16, name: 'Golero Manager', season_id: 28, image_path: 'https://cdn.sportmonks.com/images/soccer/leagues/10/74.png' },
    round: { id: 987, name: 'Speelronde 10', sequence: 10 },
    winner: { user_id: 204, name: 'Joris de Vries', points: 42, rank: 1 },
    winners: [{ user_id: 204, name: 'Joris de Vries', points: 42, rank: 1 }],
    viewer: { points: 37, rank: 4, participants_count: 25, difference_to_winner: 5 },
    best_selected_player: { player_id: 25109, name: 'Henk Veerman', image_path: 'https://cdn.sportmonks.com/images/soccer/players/21/25109.png', points: 12 },
    best_player_of_round: { player_id: 25110, name: 'Dylan Vente', image_path: 'https://cdn.sportmonks.com/images/soccer/players/21/25110.png', round_points: 16 },
  },
  {
    event_id: 502,
    type: 'fantasy_round_completed',
    league: { id: 16, name: 'Golero Manager', season_id: 28, image_path: 'https://cdn.sportmonks.com/images/soccer/leagues/10/74.png' },
    round: { id: 986, name: 'Speelronde 9', sequence: 9 },
    winner: { user_id: 311, name: 'Sarah Jansen', points: 51, rank: 1 },
    winners: [{ user_id: 311, name: 'Sarah Jansen', points: 51, rank: 1 }],
    viewer: { points: 44, rank: 3, participants_count: 25, difference_to_winner: 7 },
    best_selected_player: { player_id: 25111, name: 'Ragnar Oratmangoen', image_path: 'https://cdn.sportmonks.com/images/soccer/players/21/25111.png', points: 15 },
    best_player_of_round: { player_id: 25112, name: 'Liam van Gelderen', image_path: 'https://cdn.sportmonks.com/images/soccer/players/21/25112.png', round_points: 18 },
  },
]

const MOCK_PENDING: PendingCelebrationsResponse = {
  matches: MOCK_MATCHES
    .filter((match) => match.user_prediction?.points_awarded === 10)
    .map((match) => ({
      match,
      user_prediction: match.user_prediction,
      poule: { name: match.poule_id === 101 ? 'Vriendenpoule' : 'KKD 2026/2027' },
    })),
  badge_events: [{ badge: { name: 'Scherpschutter' } }],
  quiz_outcome_events: [],
  fantasy_round_events: MOCK_MANAGER_EVENTS,
}

export function SuccessOverviewPage() {
  const { locale, t } = useTranslation()
  const queryClient = useQueryClient()
  const searchParams = new URLSearchParams(window.location.search)
  const debugParam = searchParams.get('debug')
  const mockMode = import.meta.env.DEV && (debugParam === 'success' || searchParams.get('mock') === 'success')
  const homeQuery = useHomeBootstrap(locale, !mockMode)
  const recentMatchesQuery = useRecentMatches(locale, !mockMode)
  const pendingQuery = useQuery({
    queryKey: ['pending-celebrations'],
    queryFn: () => getPendingCelebrations(),
    enabled: !mockMode,
    staleTime: Infinity,
    retry: 1,
  })
  const celebrationStartedRef = useRef(false)
  const debugCelebrationStartedRef = useRef(false)
  const [activeView, setActiveView] = useState<DashboardView>(mockMode ? 'success' : 'dashboard')
  const [celebrationAvailable, setCelebrationAvailable] = useState(mockMode)
  const debugConfetti =
    import.meta.env.DEV &&
    (debugParam === 'confetti' || debugParam === 'success')

  useEffect(() => {
    if (!debugConfetti || debugCelebrationStartedRef.current) return
    debugCelebrationStartedRef.current = true
    void showBrowserConfetti()
  }, [debugConfetti])

  useEffect(() => {
    if (mockMode) return
    if (pendingQuery.data && hasPendingCelebrations(pendingQuery.data)) {
      setCelebrationAvailable(true)
      setActiveView('success')
    }
  }, [mockMode, pendingQuery.data])

  useEffect(() => {
    const pending = mockMode ? undefined : pendingQuery.data
    if (!pending || !hasPendingCelebrations(pending) || celebrationStartedRef.current) return

    celebrationStartedRef.current = true
    void showBrowserConfetti()
      .then(() => markCelebrationsSeen())
      .then(() => {
        queryClient.setQueryData(['pending-celebrations'], {
          matches: [],
          badge_events: [],
          quiz_outcome_events: [],
          fantasy_round_events: [],
        })
      })
      .catch((error: unknown) => {
        celebrationStartedRef.current = false
        if (import.meta.env.DEV) {
          console.warn('[celebrations] Could not complete celebration flow', error)
        }
      })
  }, [mockMode, pendingQuery.data, queryClient])

  const hasPendingCelebration = Boolean(pendingQuery.data && hasPendingCelebrations(pendingQuery.data))

  const dashboard = mockMode ? MOCK_DASHBOARD : homeQuery.data?.dashboard
  const recentMatches = mockMode ? MOCK_RECENT_MATCHES : (recentMatchesQuery.data ?? null)
  const pending = mockMode ? MOCK_PENDING : pendingQuery.data
  const managerEvents = pending?.fantasy_round_events ?? []
  const loading = !mockMode && (homeQuery.isLoading || recentMatchesQuery.isLoading || pendingQuery.isLoading)
  const hasAnyData = Boolean(dashboard || recentMatches || pending)
  const pendingPerfectCelebrations = getPerfectCelebrations(pending)
  const perfectCelebrations = pendingPerfectCelebrations.length > 0
    ? pendingPerfectCelebrations
    : getPerfectCelebrationsFromRecent(recentMatches)

  const hasAvailableCelebration = mockMode || celebrationAvailable || hasPendingCelebration
  const displayedView = hasPendingCelebration && !celebrationAvailable ? 'success' : activeView

  if (displayedView === 'dashboard' || !hasAvailableCelebration) {
    return (
      <div className="space-y-5">
        {hasAvailableCelebration ? (
          <DashboardViewToggle activeView="dashboard" hasSuccess onChange={setActiveView} />
        ) : null}
        <GameModesPage />
      </div>
    )
  }

  if (loading && !hasAnyData) {
    return (
      <div className="space-y-6">
        <LoadingScreen compact />
        <SuccessOverviewSkeleton />
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-10">
      <DashboardViewToggle activeView="success" hasSuccess onChange={setActiveView} />
      <h1 className="text-lg font-black tracking-tight text-white sm:text-xl">{t('Dagoverzicht')}</h1>

      {!mockMode && homeQuery.isError ? <ErrorCard message={t('Het poule-overzicht kon niet worden geladen.')} onRetry={() => void homeQuery.refetch()} /> : null}
      {!mockMode && recentMatchesQuery.isError ? <ErrorCard message={t('De recente uitslagen konden niet worden geladen.')} onRetry={() => void recentMatchesQuery.refetch()} /> : null}
      {!mockMode && pendingQuery.isError ? <ErrorCard message={t('De recente successen konden niet worden geladen.')} onRetry={() => void pendingQuery.refetch()} /> : null}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.08fr)_minmax(320px,0.92fr)]">
        <div className="space-y-4">
          {pending && hasPendingCelebrations(pending) ? <RecentSuccesses pending={pending} /> : null}
          {dashboard ? <PouleSuccessSection dashboard={dashboard} recent={recentMatches} perfectCelebrations={perfectCelebrations} /> : null}
        </div>
        <div className="space-y-4">
          {managerEvents.length > 0 ? <ManagerSuccessSection events={managerEvents} /> : null}
        </div>
      </div>

      {!dashboard && managerEvents.length === 0 && (!pending || !hasPendingCelebrations(pending)) ? (
        <EmptySuccessState />
      ) : null}
    </div>
  )
}

function DashboardViewToggle({
  activeView,
  hasSuccess,
  onChange,
}: {
  activeView: DashboardView
  hasSuccess: boolean
  onChange: (view: DashboardView) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="mt-2 flex justify-center">
      <div
        aria-label={t('Dashboardweergave')}
        className="inline-flex rounded-full border border-white/[0.08] bg-[#0d1113] p-1 shadow-lg shadow-black/10"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeView === 'dashboard'}
          onClick={() => onChange('dashboard')}
          className={`rounded-full px-4 py-2 text-xs font-bold transition ${activeView === 'dashboard' ? 'bg-white text-[#0d1113]' : 'text-surface-400 hover:text-white'}`}
        >
          {t('Dashboard')}
        </button>
        {hasSuccess ? (
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'success'}
            onClick={() => onChange('success')}
            className={`rounded-full px-4 py-2 text-xs font-bold transition ${activeView === 'success' ? 'bg-accent-300 text-[#0d1113]' : 'text-surface-400 hover:text-white'}`}
          >
            {t('Dagoverzicht')}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function RecentSuccesses({ pending }: { pending: PendingCelebrationsResponse }) {
  const { t } = useTranslation()
  const badgeEvents = pending.badge_events
  const matchCount = pending.matches.length
  const fantasyEvents = pending.fantasy_round_events
  const quizEvents = pending.quiz_outcome_events

  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <span className="text-2xl" aria-hidden="true">🎉</span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200">{t('Nieuw')}</p>
          <h2 className="mt-1 text-lg font-black tracking-tight text-white sm:text-xl">{t('Je nieuwste successen')}</h2>
          <p className="mt-1 text-sm text-surface-400">{t('Deze resultaten stonden klaar sinds je vorige bezoek.')}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {matchCount > 0 ? <SuccessCount value={matchCount} label={t('Perfecte uitslagen')} icon="⚽" /> : null}
        {badgeEvents.length > 0 ? <SuccessCount value={badgeEvents.length} label={t('Nieuwe trofeeën')} icon="🏆" /> : null}
        {fantasyEvents.length > 0 ? <SuccessCount value={fantasyEvents.length} label={t('Manager-rondes')} icon="⭐" /> : null}
        {quizEvents.length > 0 ? <SuccessCount value={quizEvents.length} label={t('Trivia-resultaten')} icon="🧠" /> : null}
      </div>

      {badgeEvents.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {badgeEvents.slice(0, 8).map((event, index) => {
            const record = asRecord(event)
            const badge = asRecord(record?.badge)
            const name = readString(badge?.name) ?? readString(record?.poule_name) ?? t('Nieuwe trofee')
            return (
              <span key={`${name}-${index}`} className="rounded-full border border-amber-300/15 bg-amber-300/[0.08] px-3 py-1.5 text-xs font-semibold text-amber-100">
                {readString(badge?.icon_url) ? '🏅 ' : '🏅 '}{name}
              </span>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}

function PouleSuccessSection({ dashboard, recent, perfectCelebrations }: { dashboard: Dashboard; recent: HomeRecentMatches | null; perfectCelebrations: PerfectCelebration[] }) {
  const { t } = useTranslation()
  const [isPerfectResultsOpen, setIsPerfectResultsOpen] = useState(false)
  const poules = dashboard.poules ?? []
  const matches = poules.flatMap((poule) => poule.matches ?? [])
  const bestPoule = useMemo(() => {
    return poules
      .map((poule) => ({ poule, points: pointsForMatches(poule.matches ?? []) }))
      .sort((left, right) => right.points - left.points)[0]
  }, [poules])
  const dateLabel = formatDashboardDate(recent?.date ?? dashboard.date, t)
  const totalPoints = dashboard.total_points ?? pointsForMatches(matches)
  const predictedMatches = dashboard.matches_count ?? matches.filter((match) => match.user_prediction).length
  const perfectPredictions = dashboard.prediction_counts?.perfect ?? matches.filter((match) => match.user_prediction?.points_awarded === 10).length
  const wrongPredictions = dashboard.prediction_counts?.wrong ?? matches.filter((match) => match.user_prediction?.points_awarded === 0).length

  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
      <SectionHeading title={t('Voetbalpoules')} subtitle={dateLabel} />

      <div className="mt-5 rounded-[1.1rem] bg-[#111416] px-4 py-4">
        <p className="text-sm text-surface-400">{t('Jouw poule-resultaat')}</p>
        <div className="mt-2 flex items-end gap-2">
          <strong className="text-lg font-black tracking-tight text-white sm:text-xl">{totalPoints}</strong>
          <span className="pb-1 text-sm font-semibold text-surface-400">{t('punten')}</span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 divide-x divide-white/[0.06] rounded-[1.1rem] bg-[#111416] px-2 py-3 text-center">
        <Stat value={predictedMatches} label={t('Wedstrijden')} />
        <Stat value={perfectPredictions} label={t('Perfect')} />
        <Stat value={wrongPredictions} label={t('Volledig fout')} />
      </div>

      {bestPoule?.poule && bestPoule.points > 0 ? (
        <HighlightRow
          label={t('Beste poule')}
          value={bestPoule.poule.name}
          detail={`${bestPoule.points} ${t('punten')}`}
          to={`/poules/${bestPoule.poule.id}`}
          imageUrl={bestPoule.poule.thumbnail_url ?? undefined}
          icon="🛡️"
        />
      ) : null}
      {perfectCelebrations.length > 0 ? (
        <PerfectResults items={perfectCelebrations} onOpenAll={() => setIsPerfectResultsOpen(true)} />
      ) : <EmptyInline text={t('Nog geen perfecte uitslagen.')} />}

      {dashboard.badges_earned_today && dashboard.badges_earned_today.length > 0 ? (
        <div className="mt-5 border-t border-white/[0.07] pt-5">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-surface-500">{t('Nieuwe trofeeën')}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {dashboard.badges_earned_today.map((badge, index) => (
              <span key={`${badge.name ?? 'badge'}-${index}`} className="rounded-full bg-white/[0.06] px-3 py-1.5 text-xs font-semibold text-surface-200">
                {badge.icon_url ? '🏅 ' : ''}{badge.name ?? t('Trofee')}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {isPerfectResultsOpen ? <PerfectResultsSheet items={perfectCelebrations} onClose={() => setIsPerfectResultsOpen(false)} /> : null}
    </section>
  )
}

function ManagerSuccessSection({ events }: { events: unknown[] }) {
  const { t } = useTranslation()

  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
      <SectionHeading title={t('Golero Manager')} subtitle={t('Jouw nieuwste Manager-resultaten')} />

      <div className="mt-5 space-y-3">
        {events.slice(0, 1).map((event, index) => <ManagerDayScoreCard key={`${readString(asRecord(event)?.event_id) ?? index}`} event={event} />)}
      </div>
    </section>
  )
}

function ManagerDayScoreCard({ event }: { event: unknown }) {
  const { t } = useTranslation()
  const record = asRecord(event)
  const league = asRecord(record?.league)
  const round = asRecord(record?.round)
  const viewer = asRecord(record?.viewer)
  const winner = asRecord(record?.winner) ?? {}
  const winners = Array.isArray(record?.winners) ? record.winners.map(asRecord).filter((item): item is Record<string, unknown> => item !== null) : []
  const bestSelectedPlayer = asRecord(record?.best_selected_player)
  const bestPlayerOfRound = asRecord(record?.best_player_of_round)
  const leagueId = readNumber(league?.id)
  const winnerNames = (winners.length > 0 ? winners : [winner])
    .map((item) => readString(item?.name))
    .filter((name): name is string => Boolean(name))
    .join(', ')
  const content = (
    <article className="rounded-[1rem] bg-[#111416] p-4 transition hover:bg-[#171a1c]">
      <div className="flex items-start gap-3">
        {readString(league?.image_path) ? <img src={readString(league?.image_path) ?? undefined} alt="" className="h-9 w-9 shrink-0 rounded-full object-contain" /> : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-300/10 text-lg" aria-hidden="true">🏆</span>}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-200">{t('Speelronde afgerond')}</p>
          <p className="mt-1 truncate text-sm font-black text-white">{readString(league?.name) ?? t('Golero Manager')}</p>
          <p className="mt-0.5 truncate text-xs text-surface-500">{readString(round?.name) ?? t('Speelronde')}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <ManagerDayScoreStat label={t('Jouw score')} value={`${readNumber(viewer?.points) ?? 0} ${t('punten')}`} detail={readNumber(viewer?.rank) ? `${t('plek')} ${readNumber(viewer?.rank)}` : undefined} />
        <ManagerDayScoreStat label={t('Dagwinnaar')} value={winnerNames || t('Onbekend')} detail={readNumber(winner?.points) == null ? undefined : `${readNumber(winner?.points)} ${t('punten')}`} />
      </div>

      {readNumber(viewer?.difference_to_winner) != null ? (
        <p className="mt-3 text-center text-xs text-surface-500">{t('Verschil met winnaar')}: {readNumber(viewer?.difference_to_winner)} {t('punten')}</p>
      ) : null}

      {bestSelectedPlayer || bestPlayerOfRound ? (
        <div className="mt-4 grid gap-3 border-t border-white/[0.06] pt-3">
          {bestSelectedPlayer ? <ManagerPlayerHighlight label={t('Beste geselecteerde speler')} player={bestSelectedPlayer} pointsKey="points" /> : null}
          {bestPlayerOfRound ? <ManagerPlayerHighlight label={t('Beste speler van de ronde')} player={bestPlayerOfRound} pointsKey="round_points" /> : null}
        </div>
      ) : null}
    </article>
  )
  return leagueId ? <Link to={`/dashboard/manager/competities/${leagueId}`} className="block">{content}</Link> : content
}

function ManagerDayScoreStat({ label, value, detail }: { label: string; value: string; detail?: string | undefined }) {
  return (
    <div className="rounded-[0.9rem] bg-[#0b0f10] p-3">
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-surface-500">{label}</p>
      <p className="mt-1 truncate text-sm font-black text-white">{value}</p>
      {detail ? <p className="mt-0.5 text-[11px] font-semibold text-cyan-200">{detail}</p> : null}
    </div>
  )
}

function ManagerPlayerHighlight({ label, player, pointsKey }: { label: string; player: Record<string, unknown>; pointsKey: string }) {
  const { t } = useTranslation()
  const imagePath = readString(player.image_path)
  const points = readNumber(player[pointsKey])
  return (
    <div className="flex min-w-0 items-center gap-2">
      {imagePath ? <img src={imagePath} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" /> : <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-sm" aria-hidden="true">⭐</span>}
      <div className="min-w-0">
        <p className="truncate text-[10px] font-bold uppercase tracking-[0.12em] text-surface-500">{label}</p>
        <p className="truncate text-xs font-bold text-white">{readString(player.name) ?? t('Onbekende speler')}</p>
      </div>
      {points != null ? <span className="ml-auto shrink-0 text-xs font-black text-cyan-200">{points} {t('punten')}</span> : null}
    </div>
  )
}

function PerfectResults({ items, onOpenAll }: { items: PerfectCelebration[]; onOpenAll: () => void }) {
  const { t } = useTranslation()
  const previewItems = items.slice(0, 2)
  const remainingCount = items.length - previewItems.length

  return (
    <div className="mt-5 border-t border-white/[0.07] pt-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-surface-500">{t('Perfecte uitslagen')}</p>
        {remainingCount > 0 ? (
          <button
            type="button"
            onClick={onOpenAll}
            className="rounded-full bg-white/[0.06] px-2.5 py-1 text-xs font-black text-surface-200 transition hover:bg-white/[0.1] hover:text-white"
          >
            +{remainingCount}
          </button>
        ) : null}
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {previewItems.map((item) => <PerfectResultCard key={`${item.pouleName}-${item.match.id}`} item={item} />)}
      </div>
    </div>
  )
}

function PerfectResultCard({ item }: { item: PerfectCelebration }) {
  const { t } = useTranslation()
  const { match, pouleName } = item

  return (
    <Link to={getMatchDetailPath(match)} className="rounded-[1rem] bg-[#0b0f10] p-3 transition hover:bg-[#151a1c]">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <TeamLogo name={match.home_team?.name ?? '—'} logoUrl={match.home_team?.logo_url ?? undefined} />
          <span className="min-w-0 truncate text-xs font-semibold text-white">{match.home_team?.name ?? '—'}</span>
        </div>
        <span className="text-base font-black tracking-tight text-white">{formatScore(match)}</span>
        <div className="flex min-w-0 items-center justify-end gap-2">
          <span className="min-w-0 truncate text-right text-xs font-semibold text-white">{match.away_team?.name ?? '—'}</span>
          <TeamLogo name={match.away_team?.name ?? '—'} logoUrl={match.away_team?.logo_url ?? undefined} />
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 border-t border-white/[0.06] pt-2">
        <span className="truncate text-[10px] text-surface-500">{pouleName}</span>
        <span className="shrink-0 text-[11px] font-black text-accent-200">{pointsForMatch(match)} {t('pt')}</span>
      </div>
    </Link>
  )
}

function getMatchDetailPath(match: SuccessMatch) {
  if (typeof match.poule_id === 'number' && typeof match.poule_match_id === 'number') {
    const params = new URLSearchParams({
      pouleMatchId: String(match.poule_match_id),
      sourceTab: 'dashboard',
    })
    return `/poules/${match.poule_id}/wedstrijden/${match.id}?${params.toString()}`
  }

  return `/wedstrijden/${match.id}`
}

function PerfectResultsSheet({ items, onClose }: { items: PerfectCelebration[]; onClose: () => void }) {
  const { t } = useTranslation()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={onClose}
        aria-label={t('Sluiten')}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="perfect-results-sheet-title"
        className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[88dvh] w-full max-w-xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-white/[0.07] p-5 sm:p-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Voetbalpoules')}</p>
            <h2 id="perfect-results-sheet-title" className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">{t('Perfecte uitslagen')}</h2>
            <p className="mt-1 text-sm text-surface-400">{items.length} {t('wedstrijden')}</p>
          </div>
          <ModalCloseButton onClick={onClose} />
        </header>

        <div className="min-h-0 overflow-y-auto p-5 sm:p-6">
          <div className="grid gap-2">
            {items.map((item) => <PerfectResultCard key={`${item.pouleName}-${item.match.id}`} item={item} />)}
          </div>
        </div>
      </section>
    </div>
  )
}

function HighlightRow({ label, value, detail, icon, imageUrl, to }: { label: string; value: string; detail: string; icon: string; imageUrl?: string | undefined; to?: string | undefined }) {
  const content = (
    <div className="mt-3 flex items-center gap-3 rounded-[1.1rem] bg-[#111416] px-4 py-3.5 transition hover:bg-[#171a1c]">
      {imageUrl ? <img src={imageUrl} alt="" className="h-9 w-9 shrink-0 rounded-full object-contain" /> : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-lg" aria-hidden="true">{icon}</span>}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-surface-500">{label}</p>
        <p className="mt-1 truncate text-sm font-bold text-white">{value}</p>
      </div>
      <span className="shrink-0 text-xs font-semibold text-accent-200">{detail}</span>
    </div>
  )
  return to ? <Link to={to} className="block">{content}</Link> : content
}

function SectionHeading({ eyebrow, title, subtitle }: { eyebrow?: string; title: string; subtitle?: string }) {
  return (
    <div>
      {eyebrow ? <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{eyebrow}</p> : null}
      <div className={`${eyebrow ? 'mt-1 ' : ''}`}>
        <h2 className="text-lg font-black tracking-tight text-white sm:text-xl">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-surface-400">{subtitle}</p> : null}
      </div>
    </div>
  )
}

function Stat({ value, label }: { value: number; label: string }) {
  return <div className="px-3"><p className="text-lg font-black text-white sm:text-xl">{value}</p><p className="mt-1 text-sm text-surface-500">{label}</p></div>
}

function SuccessCount({ value, label, icon }: { value: number; label: string; icon: string }) {
  return <div className="flex items-center gap-2 rounded-[1rem] bg-[#111416] p-3"><span aria-hidden="true">{icon}</span><span><strong className="block text-lg font-black text-white">{value}</strong><span className="block text-sm text-surface-500">{label}</span></span></div>
}

function ErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation()
  return <section className="rounded-2xl border border-red-300/15 bg-red-300/[0.05] p-4"><p className="text-sm text-red-100">{message}</p><button type="button" onClick={onRetry} className="mt-3 rounded-lg border border-red-200/20 px-3 py-2 text-xs font-bold text-red-100">{t('Opnieuw proberen')}</button></section>
}

function EmptySuccessState() {
  const { t } = useTranslation()
  return <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-8 text-center"><p className="text-3xl" aria-hidden="true">⚽</p><h2 className="mt-3 text-xl font-black text-white">{t('Je succesoverzicht verschijnt hier')}</h2><p className="mx-auto mt-2 max-w-md text-sm leading-6 text-surface-400">{t('Zodra je wedstrijden speelt of Manager-rondes afrondt, zie je hier je nieuwste resultaten.')}</p></section>
}

function EmptyInline({ text }: { text: string }) {
  return <p className="mt-5 rounded-xl border border-dashed border-white/[0.08] p-4 text-sm text-surface-500">{text}</p>
}

function SuccessOverviewSkeleton() {
  return <div className="space-y-4"><div className="h-32 animate-pulse rounded-[1.75rem] bg-white/[0.05]" /><div className="h-72 animate-pulse rounded-[1.5rem] bg-white/[0.05]" /><div className="h-56 animate-pulse rounded-[1.5rem] bg-white/[0.05]" /></div>
}

function getPerfectCelebrations(pending: PendingCelebrationsResponse | undefined): PerfectCelebration[] {
  return (pending?.matches ?? []).flatMap((raw) => {
    const record = asRecord(raw)
    const match = asRecord(record?.match)
    const prediction = asRecord(record?.user_prediction) ?? asRecord(match?.user_prediction)
    const matchId = readNumber(match?.id)
    const points = readNumber(prediction?.points_awarded)
    const pouleId =
      readNumber(record?.poule_id) ??
      readNumber(asRecord(record?.poule)?.id) ??
      readNumber(prediction?.poule_id) ??
      readNumber(match?.poule_id)

    if (!match || matchId == null || !isPerfectPoints(points)) return []

    return [{
      match: {
        ...match,
        poule_id: pouleId,
        user_prediction: prediction,
      } as DashboardMatch,
      pouleName: readString(record?.poule_name) ?? readString(asRecord(record?.poule)?.name) ?? 'Voetbalpoule',
    }]
  })
}

function getPerfectCelebrationsFromRecent(recent: HomeRecentMatches | null): PerfectCelebration[] {
  return (recent?.poules ?? []).flatMap((poule) =>
    (poule.matches ?? [])
      .filter((match) => isPerfectPoints(match.user_prediction?.points_awarded))
      .map((match) => ({
        match,
        pouleName: poule.name,
      })),
  )
}

function isPerfectPoints(value: number | null | undefined): value is number {
  return value === 10 || value === 20 || value === 30
}

function pointsForMatches(matches: SuccessMatch[]) {
  return matches.reduce((total, match) => total + pointsForMatch(match), 0)
}

function pointsForMatch(match: SuccessMatch) {
  return match.user_prediction?.points_awarded ?? 0
}

function formatScore(match: SuccessMatch) {
  const home = match.score?.home
  const away = match.score?.away
  return home == null || away == null ? '—' : `${home} - ${away}`
}

function TeamLogo({ name, logoUrl }: { name: string; logoUrl?: string | undefined }) {
  if (logoUrl) return <img src={logoUrl} alt="" className="h-8 w-8 shrink-0 object-contain sm:h-10 sm:w-10" />
  return <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-surface-800 text-[10px] font-black uppercase text-surface-300 sm:h-10 sm:w-10 sm:text-xs">{getInitials(name)}</span>
}

function getInitials(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function formatDashboardDate(value: string | undefined, t: (key: string, values?: Record<string, string | number>) => string) {
  if (!value) return t('Laatste afgeronde speeldag')
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const today = new Date()
  if (date.toDateString() === today.toDateString()) return t('Vandaag')
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long' }).format(date)
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function readString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value : null
}

function readNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}
